import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type Location = Database["public"]["Tables"]["locations"]["Row"];

const sanitizeLocationPayload = (location: Partial<Location>) => ({
  name: location.name ?? "",
  area: location.area ?? "Nairobi",
  image: location.image ?? "",
  property_count: location.property_count ?? "0",
  is_featured: location.is_featured ?? false,
});

export const useLocations = (featured?: boolean) => {
  return useQuery({
    queryKey: ["locations", featured],
    queryFn: async () => {
      let query = supabase.from("locations").select("*").order("name");
      if (featured !== undefined) query = query.eq("is_featured", featured);
      const { data, error } = await query;
      if (error) throw error;
      return data as Location[];
    },
  });
};

export const useLocationBySlug = (slug?: string) => {
  return useQuery({
    queryKey: ["locations", "detail", slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data, error } = await supabase.from("locations").select("*").eq("slug", slug).maybeSingle();
      if (error) throw error;
      return data as Location | null;
    },
  });
};

export const useLocationMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["locations"] });
    qc.invalidateQueries({ queryKey: ["properties"] });
  };

  const create = useMutation({
    mutationFn: async (location: Partial<Location>) => {
      const { data, error } = await supabase.from("locations").insert(sanitizeLocationPayload(location)).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Location> & { id: string }) => {
      const { data, error } = await supabase
        .from("locations")
        .update({ ...sanitizeLocationPayload(updates), updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("locations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
